import React from "react";
import styled, { createGlobalStyle } from "styled-components";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PostFieldLabel } from "components/Posts/PostForm/PostFieldLabel/PostFieldLabel";
import { useTrackEvent } from "libs/analytics";
import { COMMENTS_TAB } from "constants/Events";
import { useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";
import { getUser } from "selectors/commonSelectors";
import { needsTeamworkUpsell } from "utils/feature-gates";
import { useSelectedRightPanelTab } from "../../../../features/post/bulk-create/bulk-create-post.context";

const WIDE_POST_FORM_MEDIA_QUERY = "(min-width: 1920px)";

interface Props {
  formTitle?: string;

  isSubmitting: boolean;
  isNewPost: boolean;
  isCollaborationAvailable: boolean;
  isPostFailedOrPosted: boolean;

  renderAccountsSelector(): React.ReactNode;

  renderMediaSelector(): React.ReactNode;

  renderContentLabel?(): React.ReactNode;

  renderFields(): React.ReactNode;

  renderFieldMessages?(): React.ReactNode;

  renderPreview(): React.ReactNode;

  renderCollaboration(): React.ReactNode;

  renderActions(): React.ReactNode;
}

interface defaultTabProps {
  isNewPost: boolean;
  isCollaborationAvailable: boolean;
  isPostFailedOrPosted: boolean;
  upsellTeamwork: boolean;
}

export type TabView = "postPreview" | "postComments";

const getDefaultTab = (props: defaultTabProps): TabView => {
  const {
    isCollaborationAvailable,
    isPostFailedOrPosted,
    isNewPost,
    upsellTeamwork,
  } = props;

  if (isNewPost) {
    return "postPreview";
  }

  if (isCollaborationAvailable && !isPostFailedOrPosted && !upsellTeamwork) {
    return "postComments";
  }

  return "postPreview";
};

export interface Label {
  value: string;
  label: string;
  isCampaign: boolean;
  emoji?: string;
  color?: string;
}

export function PostFormBaseTemplate(props: Props): JSX.Element {
  const user = useSelector(getUser);
  const showBothRightPanels = useMediaQuery(WIDE_POST_FORM_MEDIA_QUERY);
  const defaultView = getDefaultTab({
    isCollaborationAvailable: props.isCollaborationAvailable,
    isNewPost: props.isNewPost,
    isPostFailedOrPosted: props.isPostFailedOrPosted,
    upsellTeamwork: needsTeamworkUpsell(user),
  });
  const { setTabView, tabView } = useSelectedRightPanelTab(defaultView);

  const trackEvent = useTrackEvent();

  const handleTabChange = (view: string) => {
    setTabView(view as TabView);

    if (view === "postComments") {
      trackEvent({ eventName: COMMENTS_TAB });
    }
  };

  return (
    <PostFormTemplateRoot>
      <BlockOutsideClick />
      <Body
        $showBothRightPanels={showBothRightPanels}
        data-layout={showBothRightPanels ? "dual-pane" : "tabbed"}
        data-testid="post-form-body"
      >
        {props.isSubmitting && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
            <Spinner size="lg" />
          </div>
        )}
        <div
          className="flex min-h-0 min-w-0 flex-col bg-white"
          data-testid="post-form-composer"
        >
          {props.formTitle && (
            <div
              className="flex min-h-14 flex-none basis-14 items-center border-0 px-6 py-3 shadow-[inset_0_-1px_0_#e6e6e6]"
              data-testid="post-form-header"
            >
              <h2 className="m-0 font-gabarito text-2xl font-bold leading-8 text-neutral-800">
                {props.formTitle}
              </h2>
            </div>
          )}
          <div className="min-h-0 min-w-0 flex-1 overflow-y-auto px-6 py-4">
            <SetupFields>
              <FormFields>
                <FormLine label="Accounts">
                  {props.renderAccountsSelector()}
                </FormLine>

                <FormLine label="Media" className="media-form-line">
                  {props.renderMediaSelector()}
                </FormLine>
                {props?.renderContentLabel && (
                  <FormLine label="Labels">
                    {props.renderContentLabel()}
                  </FormLine>
                )}
              </FormFields>
            </SetupFields>
            <Fields data-testid="post-form-fields">
              {props.renderFields()}
            </Fields>
            {props.renderFieldMessages?.()}
          </div>
        </div>

        {showBothRightPanels ? (
          <>
            <Preview data-testid="post-form-preview">
              <WidePanelHeader>
                <i className="fa-regular fa-play" aria-hidden="true" />
                <h3>Preview</h3>
              </WidePanelHeader>
              <PreviewBody>{props.renderPreview()}</PreviewBody>
            </Preview>
            <Preview className="!bg-white" data-testid="post-form-activity">
              <WidePanelHeader $activity>
                <i className="fa-regular fa-comment" aria-hidden="true" />
                <h3>Activity</h3>
              </WidePanelHeader>
              <PreviewBody>{props.renderCollaboration()}</PreviewBody>
            </Preview>
          </>
        ) : (
          <Preview data-testid="post-form-preview">
            <Tabs
              value={tabView}
              onValueChange={handleTabChange}
              className="flex h-full min-h-0 flex-col"
            >
              <PreviewHeader>
                <TabsList size="sm" className="w-[197px]">
                  <TabsTrigger
                    value="postPreview"
                    id="preview-tab-title"
                    className="w-[94px] rounded-[5px] [&_i]:inline-flex [&_i]:size-[15px] [&_i]:items-center [&_i]:justify-center"
                  >
                    <i className="fa-solid fa-play" aria-hidden="true" />
                    Preview
                  </TabsTrigger>
                  <TabsTrigger
                    value="postComments"
                    id="comments-tab-title"
                    className="w-[91px] rounded-[5px] [&_i]:inline-flex [&_i]:size-[15px] [&_i]:items-center [&_i]:justify-center"
                  >
                    <i className="fa-regular fa-comment" aria-hidden="true" />
                    Activity
                  </TabsTrigger>
                </TabsList>
              </PreviewHeader>
              <PreviewBody>
                <TabsContent value="postPreview" className="mt-0">
                  {props.renderPreview()}
                </TabsContent>
                <TabsContent value="postComments" className="mt-0">
                  {props.renderCollaboration()}
                </TabsContent>
              </PreviewBody>
            </Tabs>
          </Preview>
        )}
      </Body>

      <div
        className="flex min-h-14 min-w-0 flex-none basis-14 items-center justify-between gap-2 border-0 bg-white px-6 py-3 shadow-[inset_0_1px_0_#e6e6e6,0_0_4px_rgb(16_24_40/10%),0_0_2px_rgb(16_24_40/6%)] [&>:last-child]:min-w-0 [&>:last-child]:flex-wrap [&>:last-child]:gap-y-2"
        data-testid="post-form-actions"
      >
        {props.renderActions()}
      </div>
    </PostFormTemplateRoot>
  );
}

const Fields = styled.div.attrs({
  className:
    "flex min-w-0 flex-col rounded-lg border-0 shadow-[inset_0_0_0_1px_#e6e6e6,0_1px_2px_rgba(16,24,40,0.05)]",
})`
  min-width: 0;
  overflow: visible;
  padding: 1px;
`;

const SetupFields = styled.div`
  min-width: 0;
  overflow: visible;
  margin-bottom: 16px;
`;

const PreviewHeader = styled.div`
  display: flex;
  min-height: 56px;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 0;
  box-shadow: inset 0 -1px 0 #e6e6e6;
  background: #edeaff;
`;

const WidePanelHeader = styled.div<{ $activity?: boolean }>`
  display: flex;
  min-height: 56px;
  align-items: center;
  gap: 8px;
  padding: 12px ${(props) => (props.$activity ? "24px" : "16px")};
  box-shadow: inset 0 -1px 0 #e6e6e6;
  background: #edeaff;

  i {
    display: inline-flex;
    width: 20px;
    height: 20px;
    align-items: center;
    justify-content: center;
    color: #333;
    font-size: 16px;
    line-height: 20px;
  }

  h3 {
    margin: 0;
    color: #333;
    font-family: Gabarito, sans-serif;
    font-size: 20px;
    font-weight: 700;
    line-height: 24px;
  }
`;

const Preview = styled.div.attrs({
  className: "min-w-0",
})`
  display: flex;
  flex-direction: column;
  width: 432px;
  min-width: 432px;
  min-height: 0;
  overflow: hidden;
  background: #fcfaff;
  border-left: 0;
  box-shadow: inset 1px 0 0 #e6e6e6;
`;

const Body = styled.div<{ $showBothRightPanels: boolean }>`
  display: grid;
  flex: 1 1 auto;
  grid-template-columns: ${(props) =>
    props.$showBothRightPanels
      ? "minmax(0, 1fr) 432px 432px"
      : "minmax(0, 1fr) 432px"};
  gap: 0;
  min-height: 0;
  overflow: hidden;
  position: relative;
`;

const PostFormTemplateRoot = styled.div`
  pointer-events: visible;
  width: 100%;
  height: 100%;
  min-height: 0;
  flex-direction: column;
  display: flex;
`;

// block mouse events style
const BlockOutsideClick = createGlobalStyle`
  #sidebar, .posts-sidebar {
    pointer-events: none;
  }
`;

// it should be replaced by import { FormLine } from "ui/ecosystems/forms";
// when it moved to general design system
interface FormLineProps {
  label: string;
  children: React.ReactNode;
  className?: string;
}

export const FormLine = styled((props: FormLineProps) => {
  return (
    <FormLineRoot className={props.className}>
      <PostFieldLabel>{props.label}</PostFieldLabel>
      {props.children}
    </FormLineRoot>
  );
})``;

const FormLineRoot = styled.div`
  ${PostFieldLabel} {
    margin-bottom: 4px;
    color: #454545;
    font-family: Inter, sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
  }

  &.media-form-line ${PostFieldLabel} {
    margin-bottom: 6px;
  }
`;

export const FormFields = styled.div`
  ${FormLine}:not(:last-child) {
    margin-bottom: 16px;
  }
`;

const PreviewBody = styled.div`
  min-height: 0;
  flex: 1 1 auto;
  overflow-y: auto;
  margin: 0;
  padding: 16px;
`;
